/**
 * Ask our server for permission before the browser makes a Lightning Address
 * request (POST /api/tools/lnaddr/quota, see app/modules/ToolQuota.rb). The LNURL
 * requests themselves always go straight from the visitor's browser; this only
 * counts them, so our pages can't be used to flood an address with invoices.
 */
export type QuotaStep = "resolve" | "invoice";
export type QuotaStatus =
  | "ok"
  | "invalid"
  | "ip_limited"
  | "target_limited"
  | "unavailable";

export async function requestToolQuota(
  step: QuotaStep,
  address: string,
): Promise<QuotaStatus> {
  try {
    const response = await fetch("/api/tools/lnaddr/quota", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") ?? "",
      },
      body: JSON.stringify({ step, tested_address: address }),
    });
    if (!response.ok) return "unavailable";
    const data = await response.json();
    return (data.status as QuotaStatus) || "unavailable";
  } catch {
    return "unavailable";
  }
}

/** What to tell the visitor when the quota isn't "ok". */
export function quotaMessage(step: QuotaStep, status: QuotaStatus): string {
  switch (status) {
    case "invalid":
      return "That doesn't look like a Lightning Address (name@domain).";
    case "ip_limited":
    case "target_limited":
      return step === "invoice"
        ? "You can't request another test invoice right now. Please try again later."
        : "You can't run another test right now. Please try again later.";
    default:
      return "The tester is unavailable right now. Please try again in a few minutes.";
  }
}
